import {
  FirebaseAuthentication,
  type AuthStateChange,
  type CreateUserWithEmailAndPasswordOptions,
  type GetCurrentUserResult,
  type GetIdTokenOptions,
  type SignInResult,
  type SignInWithEmailAndPasswordOptions,
  type SignInWithGoogleOptions,
  type SignInWithOAuthOptions,
  type SendPasswordResetEmailOptions,
} from '@capacitor-firebase/authentication';
import store from 'store';
import { setAccessToken } from 'features/Auth/slice';

export type AuthStateChangeCallback = (change: AuthStateChange) => void;

const isNoAppError = (error: unknown) =>
  Boolean((error as { code?: string })?.code === 'app/no-app');

const getCurrentUser = async (): Promise<GetCurrentUserResult['user']> => {
  try {
    const { user } = await FirebaseAuthentication.getCurrentUser();
    return user ?? null;
  } catch (error) {
    if (isNoAppError(error)) return null;
    throw error;
  }
};

const getIdToken = async (forceRefresh = false): Promise<string | null> => {
  const options: GetIdTokenOptions = { forceRefresh };
  try {
    const { token } = await FirebaseAuthentication.getIdToken(options);
    const resolvedToken = token ?? null;

    if (resolvedToken) {
      store.dispatch(setAccessToken(resolvedToken));
    }

    return resolvedToken;
  } catch (error) {
    if (isNoAppError(error)) return null;
    throw error;
  }
};

const signInWithEmailAndPassword = (
  options: SignInWithEmailAndPasswordOptions
): Promise<SignInResult> => FirebaseAuthentication.signInWithEmailAndPassword(options);

const createUserWithEmailAndPassword = (
  options: CreateUserWithEmailAndPasswordOptions
): Promise<SignInResult> => FirebaseAuthentication.createUserWithEmailAndPassword(options);

const signInWithGoogle = (options?: SignInWithGoogleOptions): Promise<SignInResult> =>
  FirebaseAuthentication.signInWithGoogle(options);

const signInWithApple = (options?: SignInWithOAuthOptions): Promise<SignInResult> =>
  FirebaseAuthentication.signInWithApple(options);

const sendPasswordResetEmail = (options: SendPasswordResetEmailOptions): Promise<void> =>
  FirebaseAuthentication.sendPasswordResetEmail(options);

const signOut = (): Promise<void> => FirebaseAuthentication.signOut();

const deleteUser = (): Promise<void> => FirebaseAuthentication.deleteUser();

const addAuthStateChangeListener = (callback: AuthStateChangeCallback) =>
  FirebaseAuthentication.addListener('authStateChange', callback);

let authStateReadyPromise: Promise<void> | null = null;

const authStateReady = (): Promise<void> => {
  if (authStateReadyPromise) {
    return authStateReadyPromise;
  }

  authStateReadyPromise = new Promise(resolve => {
    let resolved = false;
    let listener: Awaited<ReturnType<typeof addAuthStateChangeListener>> | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const finish = async () => {
      if (resolved) return;
      resolved = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (listener) {
        await listener.remove();
      }
      resolve();
    };

    addAuthStateChangeListener(() => finish())
      .then(registeredListener => {
        if (resolved) {
          registeredListener.remove();
        } else {
          listener = registeredListener;
        }
      })
      .catch(error => {
        if (isNoAppError(error)) {
          resolve();
          return;
        }
        finish();
      });

    timeoutId = setTimeout(() => {
      finish();
    }, 5000);
  });

  return authStateReadyPromise;
};

const getEffectiveAuthToken = async (legacyAccessToken?: string | null): Promise<string> => {
  const firebaseUser = await getCurrentUser();

  if (firebaseUser) {
    const token = await getIdToken();
    if (token) {
      return token;
    }
    throw new Error('Firebase user is present but no ID token could be retrieved.');
  }

  if (legacyAccessToken) {
    return legacyAccessToken;
  }

  throw new Error('No authentication token available.');
};

export const authClient = {
  getCurrentUser,
  getIdToken,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithGoogle,
  signInWithApple,
  sendPasswordResetEmail,
  signOut,
  deleteUser,
  addAuthStateChangeListener,
  getEffectiveAuthToken,
  authStateReady,
};
